import { SortEnum } from "@finway-group/shared/lib/consts"
import { type AggregatePaginateResult, type CommentItem, type QueryParamsObject } from "@finway-group/shared/lib/models"
import { type PrevAndNextDocsResponse } from "@finway-group/shared/lib/models/adjacent/prevAndNextDocsResponse.interface"
import { type DeliveryNoteDTO } from "@finway-group/shared/lib/models/deliveryNote/deliveryNoteDTO.model"
import { type DeliveryNoteLinkRequest } from "@finway-group/shared/lib/models/expense/linkRequest.interface"
import { useMutation, useQuery, type UseQueryOptions } from "@tanstack/react-query"
import { useHistory } from "react-router-dom"
import { invalidatePurchaseOrderCache, invalidatePurchaseOrderDetail } from "~Features/pages/purchaseOrders/queries/purchaseOrder.queries"
import { queryClient } from "~Shared/config/reactQueryConfig"
import { i18n } from "~Shared/locales/i18n"
import { CacheEnum, GENERAL_QUERY_STALE_TIME, type QueryParams } from "~Shared/queries/consts"
import { getSortQueryObject } from "~Shared/queries/query.utils"
import { CompanyScopedRoutePathEnum } from "~Shared/router/RoutePath.enum"
import { NotificationService } from "~Shared/services"
import { NotificationDurationSec, NotificationTypeEnum } from "~Shared/services/notification.service"
import { convertSearchValueToQueryObject, isSet } from "~Shared/utils/helper.utils"
import { generatePath } from "~Shared/utils/routing.utils"
import { DeliveryNoteApiService } from "../service/deliveryNoteApi.service"
import { type DeliveryNote } from "../types/deliveryNote.interfaces"

export const defaultSortParams: QueryParams["sortParams"] = [{ sortField: "dateOfIssue", sortOption: SortEnum.DESC }]
const deliveryNoteSearchFields: Array<string> = ["deliveryNoteNumber", "description", "vendor.name", "lineItems.purchaseOrderLink.purchaseOrderNumber"]

// Query keys
export const DELIVERY_NOTE_QUERY_KEYS = {
    ALL: [CacheEnum.DELIVERY_NOTES] as const,
    LIST: (filters: QueryParams) => [CacheEnum.DELIVERY_NOTES, { filters }] as const,
    DETAIL: (id: string) => [...DELIVERY_NOTE_QUERY_KEYS.ALL, "detail", id] as const,
    MANY: (ids: Array<string>) => [CacheEnum.DELIVERY_NOTES, "many", ids] as const,
    ADJACENT: (id: string, filters: QueryParams) => [...DELIVERY_NOTE_QUERY_KEYS.ALL, "adjacent", id, filters] as const,
} as const

export const useDeliveryNoteQuery = (id: string) =>
    useQuery<DeliveryNote>({
        queryKey: DELIVERY_NOTE_QUERY_KEYS.DETAIL(id),
        queryFn: () => DeliveryNoteApiService.getDeliveryNoteById(id),
        enabled: Boolean(id),
        staleTime: GENERAL_QUERY_STALE_TIME,
        select: (data) => ({ ...data, dateOfIssue: data.dateOfIssue ? new Date(data.dateOfIssue) : new Date() }),
    })

export const useDeliveryNotesQuery = ({ page, limit, search, filter, sortParams }: QueryParams, options?: UseQueryOptions<AggregatePaginateResult<DeliveryNote>>) => {
    const searchParam = isSet(search) ? convertSearchValueToQueryObject(search!, deliveryNoteSearchFields) : undefined
    const params: QueryParamsObject = {
        ...(page !== undefined && { page }),
        ...(limit !== undefined && { limit }),
        ...(searchParam || {}),
        ...(getSortQueryObject(sortParams) || {}),
        ...(filter || {}),
    }

    return useQuery<AggregatePaginateResult<DeliveryNote>>({
        queryKey: DELIVERY_NOTE_QUERY_KEYS.LIST({ page, limit, search, filter, sortParams }),
        queryFn: ({ signal }) => DeliveryNoteApiService.fetchDeliveryNotes({ params, signal }),
        staleTime: GENERAL_QUERY_STALE_TIME,
        keepPreviousData: true,
        ...options,
    })
}

// Mutations
export const useCreateDeliveryNoteMutation = () => {
    const history = useHistory()
    return useMutation<DeliveryNote, Error, DeliveryNoteDTO>({
        mutationFn: (deliveryNote: DeliveryNoteDTO) => DeliveryNoteApiService.createDeliveryNote(deliveryNote),
        onSuccess: async (deliveryNote) => {
            // Invalidate and refetch delivery note lists
            await invalidateDeliveryNoteLists()

            NotificationService.send(
                NotificationTypeEnum.SUCCESS,
                i18n.t("notification:delivery_note.created.title"),
                i18n.t("notification:delivery_note.created.message"),
                NotificationDurationSec.REQUEST_CREATION,
                "useCreateDeliveryNoteMutation.onSuccess",
                {
                    onClick: () => history.push(generatePath(CompanyScopedRoutePathEnum.DELIVERY_NOTE_DETAIL, { id: deliveryNote._id })),
                    text: i18n.t("notification:delivery_note.created.link"),
                },
            )
        },
        onError: (error) => {
            NotificationService.showErrorNotificationBasedOnResponseError(error, i18n.t("error:error"))
        },
    })
}
export const useUpdateDeliveryNoteMutation = () =>
    useMutation<DeliveryNote, Error, { id: string; deliveryNote: DeliveryNoteDTO }>({
        mutationFn: ({ id, deliveryNote }: { id: string; deliveryNote: DeliveryNoteDTO }) => DeliveryNoteApiService.updateDeliveryNote(id, deliveryNote),
        onSuccess: async (_, { id }) => {
            await invalidateDeliveryNoteDetail(id)
            await invalidateDeliveryNoteLists()

            NotificationService.send(
                NotificationTypeEnum.SUCCESS,
                i18n.t("notification:delivery_note.updated.title"),
                i18n.t("notification:delivery_note.updated.message"),
                NotificationDurationSec.REQUEST_CREATION,
            )
        },
        onError: (error) => {
            NotificationService.showErrorNotificationBasedOnResponseError(error, i18n.t("error:error"))
        },
    })

export const useDeleteDeliveryNoteMutation = () =>
    useMutation<DeliveryNote, Error, string>({
        mutationFn: (deliveryNoteId: string) => DeliveryNoteApiService.deleteDeliveryNote(deliveryNoteId),
        onSuccess: async () => {
            // This will invalidate all delivery note lists
            await invalidateDeliveryNoteLists()
            NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t("notification:delivery_note.deleted.title"), i18n.t("notification:delivery_note.deleted.message"))
        },
        onError: (error) => {
            NotificationService.showErrorNotificationBasedOnResponseError(error, i18n.t("error:delivery_note.delete.title"), i18n.t("error:delivery_note.delete.message"))
        },
    })

// Utility functions for cache management
export const invalidateDeliveryNoteCache = async () => {
    await queryClient.invalidateQueries({
        queryKey: DELIVERY_NOTE_QUERY_KEYS.ALL,
    })
}

export const invalidateDeliveryNoteLists = async () => {
    await queryClient.invalidateQueries({
        queryKey: DELIVERY_NOTE_QUERY_KEYS.LIST({}),
    })
}

export const invalidateDeliveryNoteDetail = async (id: string) => {
    await queryClient.invalidateQueries({
        queryKey: DELIVERY_NOTE_QUERY_KEYS.DETAIL(id),
    })
}

export const useLinkDeliveryNotePurchaseOrderMutation = () =>
    useMutation<DeliveryNote, Error, { deliveryNoteId: string; linkRequest: DeliveryNoteLinkRequest }>({
        mutationFn: ({ deliveryNoteId, linkRequest }: { deliveryNoteId: string; linkRequest: DeliveryNoteLinkRequest }) =>
            DeliveryNoteApiService.linkPurchaseOrder(deliveryNoteId, linkRequest),
        onSuccess: async (_, { deliveryNoteId, linkRequest }) => {
            NotificationService.send(
                NotificationTypeEnum.SUCCESS,
                i18n.t("notification:delivery_note.updated.title"),
                i18n.t("notification:delivery_note.updated.message"),
                NotificationDurationSec.REQUEST_CREATION,
            )
            await Promise.all([
                invalidateDeliveryNoteDetail(deliveryNoteId),
                invalidateDeliveryNoteCache(),
                invalidatePurchaseOrderCache(),
                ...linkRequest.links.map((link) => invalidatePurchaseOrderDetail(link.poId)),
                ...linkRequest.unlinks.map((unlink) => invalidatePurchaseOrderDetail(unlink.poId)),
            ])
        },
        onError: (error) => {
            NotificationService.showErrorNotificationBasedOnResponseError(error, i18n.t("error:error"))
        },
    })

export const useCreateDeliveryNoteCommentMutation = () =>
    useMutation<DeliveryNote, Error, { deliveryNoteId: string; comment: CommentItem }>({
        mutationFn: ({ deliveryNoteId, comment }: { deliveryNoteId: string; comment: CommentItem }) => DeliveryNoteApiService.createDeliveryNoteComment(deliveryNoteId, comment),
        onSuccess: (updatedDeliveryNote: DeliveryNote, { deliveryNoteId }) => {
            queryClient.setQueryData(DELIVERY_NOTE_QUERY_KEYS.DETAIL(deliveryNoteId), (cachedDN: DeliveryNote) => ({
                ...cachedDN,
                comments: updatedDeliveryNote.comments,
            }))
        },
    })

export const useUpdateDeliveryNoteCommentsMutation = () =>
    useMutation<DeliveryNote, Error, { deliveryNoteId: string; commentId: string; comment: CommentItem }>({
        mutationFn: ({ deliveryNoteId, commentId, comment }: { deliveryNoteId: string; commentId: string; comment: CommentItem }) =>
            DeliveryNoteApiService.updateDeliveryNoteComment(deliveryNoteId, commentId, comment),
        onSuccess: (updatedDeliveryNote: DeliveryNote, { deliveryNoteId }) => {
            queryClient.setQueryData(DELIVERY_NOTE_QUERY_KEYS.DETAIL(deliveryNoteId), (cachedDN: DeliveryNote) => ({
                ...cachedDN,
                comments: updatedDeliveryNote.comments,
            }))
        },
    })

export const useDeleteDeliveryNoteCommentsMutation = () =>
    useMutation<DeliveryNote, Error, { deliveryNoteId: string; commentId: string }>({
        mutationFn: ({ deliveryNoteId, commentId }: { deliveryNoteId: string; commentId: string }) => DeliveryNoteApiService.deleteDeliveryNoteComment(deliveryNoteId, commentId),
        onSuccess: (updatedDeliveryNote: DeliveryNote, { deliveryNoteId }) => {
            queryClient.setQueryData(DELIVERY_NOTE_QUERY_KEYS.DETAIL(deliveryNoteId), (cachedDN: DeliveryNote) => ({
                ...cachedDN,
                comments: updatedDeliveryNote.comments,
            }))
        },
    })

export const useAdjacentDeliveryNotesQuery = ({ id, enabled, queryParams }: { id: string; enabled: boolean; queryParams: QueryParams }) => {
    const { page, limit, search, filter, sortParams } = queryParams
    const searchParam = isSet(search) ? convertSearchValueToQueryObject(search!, deliveryNoteSearchFields) : undefined
    const params: QueryParamsObject = {
        ...(page !== undefined && { page }),
        ...(limit !== undefined && { limit }),
        ...(searchParam || {}),
        ...(getSortQueryObject(sortParams || defaultSortParams) || {}),
        ...(filter || {}),
    }
    return useQuery<PrevAndNextDocsResponse>({
        queryKey: DELIVERY_NOTE_QUERY_KEYS.ADJACENT(id, params),
        queryFn: ({ signal }) => DeliveryNoteApiService.getAdjacentDeliveryNotes({ id, params, signal }),
        enabled,
        staleTime: GENERAL_QUERY_STALE_TIME,
    })
}
